import LPressable from "@components/LPressable";
import LBox, { LBoxProps } from "@components/LBox";
import { theme } from "@theme";
import {
  FOUR_BUTTON_LAYOUT_BUTTON_WIDTH,
  BUTTON_HEIGHT,
  LOADING_SHIMMER_SPEED,
  LOADING_SHIMMER_BG_COLOR,
  LOADING_SHIMMER_HIGHLIGHT_COLOR,
} from "../../config";
import React, { useState } from "react";
import SkeletonPlaceholder from "react-native-skeleton-placeholder";

type ButtonProps = {
  children: React.ReactNode;
  onPress: () => Promise<void> | void;
  sentryEventName: string;
  width?: number;
  disabled?: boolean;
} & Omit<LBoxProps, "children">;

const Button = ({
  children,
  onPress,
  sentryEventName,
  width = FOUR_BUTTON_LAYOUT_BUTTON_WIDTH,
  height = BUTTON_HEIGHT,
  disabled = false,
  ...rest
}: ButtonProps) => {
  if (React.Children.count(children) !== 2) {
    throw new Error("Button must have exactly 2 children");
  }

  const [icon, text] = React.Children.toArray(children);
  const [isLoading, setIsLoading] = useState(false);
  const internalOnPress = async () => {
    setIsLoading(true);
    const result = onPress();
    if (result instanceof Promise) {
      await result;
    }
    setIsLoading(false);
  };

  return (
    <LPressable
      fadeEnabled
      sentryEventName={sentryEventName}
      onPress={internalOnPress}
      disabled={isLoading || disabled}
    >
      <LBox
        bg="white"
        borderRadius="m-l"
        width={width}
        height={height}
        py="s2"
        opacity={isLoading ? 0.75 : 1}
        {...rest}
      >
        <LBox flex={6 / 10} alignItems="center" justifyContent="center">
          {icon}
        </LBox>
        <LBox flex={4 / 10} alignItems="center" justifyContent="center">
          {text}
        </LBox>
      </LBox>
    </LPressable>
  );
};

const Loading = ({ width }: { width: number }) => {
  return (
    <SkeletonPlaceholder
      highlightColor={LOADING_SHIMMER_HIGHLIGHT_COLOR}
      backgroundColor={LOADING_SHIMMER_BG_COLOR}
      speed={LOADING_SHIMMER_SPEED}
      borderRadius={theme.borderRadii.max}
    >
      <SkeletonPlaceholder.Item
        width={width}
        height={BUTTON_HEIGHT}
        borderRadius={theme.borderRadii["m-l"]}
      />
    </SkeletonPlaceholder>
  );
};

export default {
  Button,
  Loading,
};
